import { CounterpartyIdentity, CounterpartyRole, isCounterpartyRecordActive } from '@rentguru/commons-utils';
import isNil from 'lodash/isNil';
import unionBy from 'lodash/unionBy';

export type CounterpartyIdentitiesAction =
  | { type: 'SHOULD_FETCH' }
  | { type: 'IS_FETCHING' }
  | { type: 'FETCHED'; payload: { identities: CounterpartyIdentity[]; roles: CounterpartyRole[] } }
  | { type: 'ERROR'; payload: { error: string } }
  | { type: 'STALE' }
  | { type: 'UPDATE_IDENTITY'; payload: { identity: CounterpartyIdentity } }
  | { type: 'UPSERT_RECORDS'; payload: { identities: CounterpartyIdentity[]; roles: CounterpartyRole[] } }
  | { type: 'DELETE_IDENTITY'; payload: { id: string; roleIds: string[] } };

export interface CounterpartyIdentitiesState {
  identities: CounterpartyIdentity[] | null;
  roles: CounterpartyRole[];
  loading: boolean;
  shouldFetch: boolean;
  error?: string;
}

export const INITIAL_COUNTERPARTY_IDENTITIES_STATE: CounterpartyIdentitiesState = {
  identities: null,
  roles: [],
  loading: false,
  shouldFetch: false,
  error: undefined,
};

/**
 * A fetch clears the request it serves when it starts: a refresh asked while it runs (a decision that taught the
 * register after the fetch read it) stays pending and runs once the fetch is over.
 */
export const counterpartyIdentitiesReducer = (
  state: CounterpartyIdentitiesState,
  action: CounterpartyIdentitiesAction
): CounterpartyIdentitiesState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      return state.shouldFetch ? state : { ...state, shouldFetch: true };
    case 'IS_FETCHING':
      return { ...state, loading: true, shouldFetch: false };
    case 'FETCHED':
      return {
        ...state,
        loading: false,
        error: undefined,
        identities: action.payload.identities,
        roles: action.payload.roles,
      };
    case 'ERROR':
      return { ...state, loading: false, error: action.payload.error };
    case 'STALE':
      return { ...state, loading: false, shouldFetch: true };
    case 'UPDATE_IDENTITY':
      return {
        ...state,
        identities: (state.identities ?? []).map((identity) =>
          identity.id === action.payload.identity.id ? action.payload.identity : identity
        ),
      };
    case 'UPSERT_RECORDS': {
      // A fetch under way read the register before this readback: it runs again once over
      const shouldFetch = state.shouldFetch || state.loading;
      if (isNil(state.identities)) {
        return { ...state, shouldFetch };
      }
      return {
        ...state,
        shouldFetch,
        identities: unionBy(action.payload.identities, state.identities, (identity) => identity.id),
        roles: unionBy(action.payload.roles, state.roles, (role) => role.id),
      };
    }
    case 'DELETE_IDENTITY':
      return {
        ...state,
        identities: (state.identities ?? []).filter((identity) => identity.id !== action.payload.id),
        roles: state.roles.filter((role) => !action.payload.roleIds.includes(role.id)),
      };
    default:
      return state;
  }
};

// A record a decision left pending matches nothing: it has not learned the transaction yet
export const hasActiveEvidenceOfTransaction = (
  record: Pick<CounterpartyRole, 'status' | '_deleted' | 'evidenceTransactionIds'>,
  transactionId: string
): boolean => isCounterpartyRecordActive(record) && (record.evidenceTransactionIds ?? []).includes(transactionId);
